import React, { useCallback, useEffect, useState } from 'react';
import { useLiveRefresh } from '../../../context/LiveUpdatesContext';
import { toast } from 'react-hot-toast';
import {
  Users, CheckCircle2, MessageSquareWarning, AlertTriangle, LayoutGrid,
} from 'lucide-react';
import {
  DataTable, Person, Avatar, StatusPill, Button, SlideOver, Select, Modal,
} from '../../../components';
import { homeAgencyService } from '../../../services/agencyService';
import {
  FilterTabs, ReasonModal, DocList, Field, errorMessage, daysLabel, dayLabel, timeRange, APPROVAL_STATE,
  BookingPill, stampOf,
} from '../../agency/shared';

/**
 * Agencies > Staff — each connected agency's carers, and THIS home's decision
 * about each one. The agency keeps the record (DBS, right to work, training,
 * availability); the home reads it and approves the person for its own site,
 * or asks the agency for changes. Only approved staff can be offered and
 * confirmed. If the agency later changes their proof, they come back here to
 * be reviewed again.
 *
 * Approval also decides whether they may record medication while on shift
 * here — off unless ticked.
 */

/**
 * Which of this home's dashboard modules the agency carer may use — during
 * their booked shifts here only (api/utils/agency.js agencyDashboardGrant).
 * The list is what the API allows an outsider: never Staff, Payroll,
 * Recruitment, Compliance, Leave approval, Agencies or settings.
 */
const DashboardAccessModal = ({ open, worker, onClose, onSaved }) => {
  const [ids, setIds] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setIds((worker?.approval?.menu_ids || []).map(Number)); }, [open, worker]);
  if (!open || !worker) return null;
  const menus = worker.dashboard_menus || [];
  const toggle = (id) => setIds((xs) => (xs.includes(id) ? xs.filter((x) => x !== id) : [...xs, id]));
  const save = async () => {
    setBusy(true);
    try {
      const res = await homeAgencyService.setWorkerDashboardAccess(worker.id, ids);
      toast.success(res.message);
      onSaved();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      isOpen
      onClose={busy ? undefined : onClose}
      title={`Dashboard access · ${worker.name}`}
      maxWidth="max-w-lg"
      footer={(
        <div className="flex justify-between items-center gap-2">
          <span className="text-[12.5px] text-[#64748B]">{ids.length} module{ids.length === 1 ? '' : 's'} ticked</span>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button onClick={save} loading={busy}>Save access</Button>
          </div>
        </div>
      )}
    >
      <div className="flex flex-col gap-3">
        <p className="text-[13px] text-[#475569] dark:text-gray-300">
          {worker.name} signs in to the dashboard with their app login ({worker.user_id}) and sees only the modules you tick, for
          this home — and <strong>only during their booked shifts here</strong> (from an hour before it starts until it ends).
          Outside a booking they see nothing of your home.
        </p>
        {!menus.length && <p className="text-[13px] text-[#94A3B8]">No modules can be given yet.</p>}
        <ul className="flex flex-col gap-1.5 max-h-[320px] overflow-y-auto pr-1">
          {menus.map((m) => {
            const on = ids.includes(Number(m.id));
            return (
              <li key={m.id}>
                <label className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer ${on ? 'border-blue-400 bg-[#F3F7FF] dark:bg-blue-500/10' : 'border-[#E3E9F3] dark:border-gray-800'}`}>
                  <input type="checkbox" checked={on} onChange={() => toggle(Number(m.id))} />
                  <span className="text-[13.5px] font-semibold text-[#0B1220] dark:text-gray-100">{m.name}</span>
                </label>
              </li>
            );
          })}
        </ul>
        <p className="text-[12px] text-[#94A3B8]">Staff records, payroll, recruitment, compliance, leave approval, agencies and settings can never be given to agency staff.</p>
      </div>
    </Modal>
  );
};

const Panel = ({ userId, onClose, onChanged }) => {
  const [w, setW] = useState(null);
  const [medication, setMedication] = useState(false);
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState(false);
  const [accessOpen, setAccessOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await homeAgencyService.worker(userId);
      setW(res.data);
      setMedication(Boolean(res.data?.approval?.can_log_medication));
    } catch (err) {
      toast.error(errorMessage(err));
    }
  }, [userId]);
  useEffect(() => { if (userId) { setW(null); load(); } }, [userId, load]);

  const approve = async () => {
    setBusy(true);
    try {
      const res = await homeAgencyService.approveWorker(userId, medication);
      toast.success(res.message);
      await load();
      onChanged();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleMedication = async (value) => {
    setMedication(value);
    if (w?.state !== 'approved') return;
    try {
      const res = await homeAgencyService.setWorkerMedication(userId, value);
      toast.success(res.message);
      onChanged();
    } catch (err) {
      setMedication(!value);
      toast.error(errorMessage(err));
    }
  };

  const meta = APPROVAL_STATE[w?.state] || APPROVAL_STATE.review;
  return (
    <SlideOver
      isOpen={Boolean(userId)}
      onClose={onClose}
      width="sm:max-w-[620px]"
      title={w?.name || 'Agency staff'}
      subtitle={w ? `${w.user_id} · ${w.job_role} · ${w.agency?.name}` : ''}
      footer={w && w.connected ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <label className="flex items-center gap-2 text-[13px] text-[#334155] dark:text-gray-300">
              <input type="checkbox" checked={medication} onChange={(e) => toggleMedication(e.target.checked)} />
              Can record medication here
            </label>
            {/* More access: dashboard modules at this home, during their
                booked shifts only. Needs approval first, like medication. */}
            <button
              type="button"
              disabled={w.state !== 'approved'}
              title={w.state !== 'approved' ? 'Approve them for this home first' : undefined}
              onClick={() => setAccessOpen(true)}
              className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-blue-600 dark:text-blue-400 hover:underline disabled:text-[#94A3B8] disabled:no-underline disabled:cursor-not-allowed"
            >
              <LayoutGrid className="w-4 h-4" />
              Dashboard access{w.approval?.menu_ids?.length ? ` (${w.approval.menu_ids.length})` : ''}
            </button>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" leftIcon={<MessageSquareWarning className="w-4 h-4" />} onClick={() => setAsking(true)}>Ask for changes</Button>
            {w.state !== 'approved' && (
              <Button leftIcon={<CheckCircle2 className="w-4 h-4" />} loading={busy} disabled={w.issues?.length > 0} onClick={approve}>Approve for this home</Button>
            )}
          </div>
        </div>
      ) : null}
    >
      {!w ? <p className="text-[13px] text-[#94A3B8]">Loading…</p> : (
        <div className="flex flex-col gap-6">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <Avatar name={w.name} image={w.avatar_url} size={44} />
              <StatusPill tone={meta.tone}>{meta.label}</StatusPill>
            </div>
            {w.approval?.decided_at && <span className="text-[12px] text-[#94A3B8]">Last decision {stampOf(w.approval.decided_at)}</span>}
          </div>
          {w.changed_since_approval && (
            <p className="text-[12.5px] text-amber-700 bg-amber-50 dark:bg-amber-500/10 rounded-xl px-3 py-2">
              The agency changed this person's proof or role since you approved them. Review it and approve again.
            </p>
          )}
          {w.state === 'changes' && w.approval?.note && (
            <p className="text-[12.5px] text-rose-700 bg-rose-50 dark:bg-rose-500/10 rounded-xl px-3 py-2">You asked: {w.approval.note}</p>
          )}
          {w.issues?.length > 0 && (
            <div className="rounded-xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 px-4 py-3">
              <p className="text-[12.5px] font-bold text-amber-800 dark:text-amber-300 mb-1 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" />Can't be approved yet</p>
              <ul className="text-[12.5px] text-amber-800 dark:text-amber-300 list-disc pl-4">{w.issues.map((i) => <li key={i}>{i}</li>)}</ul>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <Field label="Agency">{w.agency?.name}</Field>
            <Field label="Agency contact">{[w.agency?.phone, w.agency?.email].filter(Boolean).join(' · ')}</Field>
            <Field label="Role">{w.job_role}</Field>
            <Field label="Phone">{w.phone}</Field>
            <Field label="Availability">{w.accepting_offers ? daysLabel(w.available_days) : 'Paused by the agency'}</Field>
            <Field label="App login">{w.user_id}</Field>
          </div>

          <div className="flex flex-col gap-2">
            <p className="text-[13.5px] font-bold text-[#0B1220] dark:text-gray-100">DBS</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Certificate">{w.dbs?.certificate_number}</Field>
              <Field label="Level">{w.dbs?.level?.replace('_', ' + ')}</Field>
              <Field label="Issued">{w.dbs?.issue_date ? dayLabel(w.dbs.issue_date) : null}</Field>
              <Field label="Review by">{w.dbs?.review_date ? dayLabel(w.dbs.review_date) : null}</Field>
            </div>
            <DocList docs={w.dbs?.documents || []} />
          </div>
          <div className="flex flex-col gap-2">
            <p className="text-[13.5px] font-bold text-[#0B1220] dark:text-gray-100">Right to work</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Document">{w.rtw?.document_type}</Field>
              <Field label="Expiry">{w.rtw?.no_expiry ? 'No expiry' : w.rtw?.expiry_date ? dayLabel(w.rtw.expiry_date) : null}</Field>
            </div>
            <DocList docs={w.rtw?.documents || []} />
          </div>
          <div className="flex flex-col gap-2">
            <p className="text-[13.5px] font-bold text-[#0B1220] dark:text-gray-100">Training</p>
            {!w.training?.length && <p className="text-[13px] text-[#94A3B8]">None recorded.</p>}
            {(w.training || []).map((t) => (
              <div key={t.id} className="rounded-xl border border-[#E3E9F3] dark:border-gray-800 p-3 flex flex-col gap-1.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[13px] font-semibold text-[#0B1220] dark:text-gray-100">{t.name}{t.required ? <span className="ml-1.5 text-[11px] text-blue-600">Required</span> : ''}</span>
                  <span className="text-[12px] text-[#94A3B8]">{t.completed_date ? `Done ${dayLabel(t.completed_date)}` : ''}{t.expiry_date ? ` · expires ${dayLabel(t.expiry_date)}` : ''}</span>
                </div>
                <DocList docs={t.documents || []} />
              </div>
            ))}
          </div>

          {w.bookings?.length > 0 && (
            <div className="flex flex-col gap-2">
              <p className="text-[13.5px] font-bold text-[#0B1220] dark:text-gray-100">Shifts at your home</p>
              <ul className="flex flex-col gap-1.5">
                {w.bookings.map((b) => (
                  <li key={b.id} className="flex items-center justify-between gap-2 text-[13px]">
                    <span>{dayLabel(b.shift_date)} · {timeRange(b)}</span>
                    <BookingPill booking={b} />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <DashboardAccessModal
        open={accessOpen}
        worker={w}
        onClose={() => setAccessOpen(false)}
        onSaved={async () => { setAccessOpen(false); await load(); onChanged(); }}
      />
      <ReasonModal
        open={asking}
        title={`Ask ${w?.agency?.name || 'the agency'} for changes`}
        intro="They can't be offered for your shifts until the agency updates the record and you approve them."
        label="What needs changing"
        confirmLabel="Send to the agency"
        variant="primary"
        onClose={() => setAsking(false)}
        onSubmit={async (reason) => {
          const res = await homeAgencyService.requestWorkerChanges(userId, reason);
          toast.success(res.message);
          setAsking(false);
          await load();
          onChanged();
        }}
      />
    </SlideOver>
  );
};

const AgencyStaffTab = ({ agencies, agencyId, setAgencyId, onChanged }) => {
  const [state, setState] = useState('review');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const res = await homeAgencyService.workers({ agency_id: agencyId || undefined });
      setRows(res.data || []);
    } catch (err) {
      if (!quiet) toast.error(errorMessage(err, 'Could not load agency staff.'));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [agencyId]);
  useEffect(() => { load(); }, [load]);
  // The list only — an open review panel keeps the decision being typed.
  useLiveRefresh(['agency'], () => load({ quiet: true }));

  const q = search.trim().toLowerCase();
  const scoped = q ? rows.filter((r) => [r.name, r.user_id, r.agency?.name].some((v) => String(v || '').toLowerCase().includes(q))) : rows;
  const list = state === 'all' ? scoped : scoped.filter((r) => r.state === state);

  const columns = [
    { key: 'name', header: 'Staff', sortable: true, render: (r) => <Person name={r.name} sub={r.user_id} image={r.avatar_url} /> },
    { key: 'agency', header: 'Agency', render: (r) => r.agency?.name },
    { key: 'job_role', header: 'Role' },
    { key: 'availability', header: 'Availability', render: (r) => <span className="text-[12.5px]">{r.accepting_offers ? daysLabel(r.available_days) : 'Paused'}</span> },
    {
      key: 'proof', header: 'Proof',
      render: (r) => (r.issues.length ? <StatusPill tone="warn">{r.issues.length} missing</StatusPill> : <StatusPill tone="good">Complete</StatusPill>),
    },
    {
      key: 'state', header: 'Your decision',
      render: (r) => {
        const m = APPROVAL_STATE[r.state] || APPROVAL_STATE.review;
        return <StatusPill tone={m.tone}>{r.changed_since_approval ? 'Changed — review' : m.label}</StatusPill>;
      },
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <FilterTabs
          value={state}
          onChange={setState}
          tabs={[
            { value: 'review', label: 'Needs review', count: scoped.filter((r) => r.state === 'review').length, alert: true },
            { value: 'approved', label: 'Approved', count: scoped.filter((r) => r.state === 'approved').length },
            { value: 'changes', label: 'Changes requested', count: scoped.filter((r) => r.state === 'changes').length },
            { value: 'all', label: 'All', count: scoped.length },
          ]}
        />
        {agencies.length > 1 && (
          <Select
            value={agencyId ? String(agencyId) : ''}
            onChange={(v) => setAgencyId(v ? Number(v) : null)}
            options={[{ value: '', label: 'All agencies' }, ...agencies.map((a) => ({ value: String(a.id), label: a.name }))]}
            className="w-full sm:w-64"
          />
        )}
      </div>
      <DataTable
        columns={columns}
        data={list}
        loading={loading}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search name, login ID or agency…"
        rowKey={(r) => r.id}
        onRowClick={(r) => setOpenId(r.id)}
        emptyIcon={Users}
        emptyTitle={state === 'review' ? 'Nobody waiting for review' : 'No agency staff here'}
        emptyText="Staff of your connected agencies appear here for you to approve."
      />
      <Panel userId={openId} onClose={() => setOpenId(null)} onChanged={() => { load(); onChanged(); }} />
    </div>
  );
};

export default AgencyStaffTab;
